import { createSlice } from '@reduxjs/toolkit'

const initialState = {
  common: {
    formLoaded: false,
    formCanBeSubmitted: true,
    formSubmitted: false,
    isEditable: true,
    canSendSubscription: false,
    isRetracted: false,
    saveAction: '',
    subscriptionStatus: '',
    lastSentDate: '',
    realStatus: '',
    id: '',
    token: '',
    saveUrl: '',
    statusErrors: [],
    errors: {},
    mandatoryFields: {},
    missingFieldsDetails: {},
    createdByUserId: '',
  },
  zohoModule: {
    isValidZohoId: true,
  },
  customerModule: {
    newsletterData: {},
    newsletterLoading: false,
    emailIsLoading: false,
    prefilledEmail: {
      status: false,
      message: '',
    },
    siretIsLoading: false,
    siretIsValid: false,
    italyFiscalCode: {
      isLoading: false,
      codeIsValid: null,
      errorMessage: '',
    },
  },
  characteristicsModule: {
    addressLoading: false,
    addressList: [],
    cityEligibility: {},
    cityEligibilityIsLoading: false,
  },
  bankingModule: {
    isBicEditable: true,
    ibanValidationData: {},
  },
  electricityMeterModule: {
    isValidPdl: false,
    pdlEligibilityPending: false,
    pdlEligibilityLoading: false,
    pdlEligibilityData: {},
    triggerSearchPod: false,
    searchPodResponses: null,
    oldOccupant: '',
    showPdlConfirmation: false,
  },
  gasMeterModule: {
    pceEligibilityLoading: false,
    pceEligibilityPending: false,
    isValidPce: false,
    pceEligibilityData: {},
  },
  providerConfig: {
    allowCdfOnMes: 0,
    allowDisclaimerCommitmentPro: 0,
    isIntegrated: false,
    providerWorkflow: '',
    debordementCallStatus: '',
    debordementCallLeadSource: '',
    mesEmergencyExpress: false,
    mesEmergencyUrgent: false,
  },
  offerConfig: {},
  offerModule: {
    offerIsAvailable: true,
    offerLoading: false,
    providerLoading: false,
    offers: {},
    providers: {},
    providerOptions: {},
  },
  consumptionModule: {
    offerPriceLoading: false,
    prefilled: {
      carBase: {
        status: false,
        message: '',
      },
      carGas: {
        status: false,
        message: '',
      },
      carHp: {
        status: false,
        message: '',
      },
      carHc: {
        status: false,
        message: '',
      },
    },
  },
  propaneModule: {
    energySuppliers: {},
    quoteReasonsList: {},
    propaneVisibility: {},
  },
  additionalSales: {
    partnerCallbackTries: 0,
    tabToShow: 'leadAssistance',
    shouldScroll: false,
    sendingCode: false,
    sendCodeError: null,
    sendCodeSuccess: false,
    openingSignLink: false,
  },
}

const initialFormConfigurationUpdateStateHandler = (state, action) => {
  state.common = {
    ...initialState.common,
    formLoaded: true,
    ...action.payload,
  }
  state.zohoModule = { ...initialState.zohoModule }
  state.customerModule = { ...initialState.customerModule }
  state.characteristicsModule = { ...initialState.characteristicsModule }
  state.bankingModule = { ...initialState.bankingModule }
  state.electricityMeterModule = { ...initialState.electricityMeterModule }
  state.gasMeterModule = { ...initialState.gasMeterModule }
  state.providerConfig = { ...initialState.providerConfig }
  state.offerConfig = { ...initialState.offerConfig }
  state.offerModule = { ...initialState.offerModule }
  state.consumptionModule = { ...initialState.consumptionModule }
  state.propaneModule = { ...initialState.propaneModule }
}

const addCommonFormErrorsHandler = (state, action) => {
  state.common = {
    ...state.common,
    errors: {
      ...state.common.errors,
      ...action.payload,
    },
  }
}

const deleteCommonFormErrorsHandler = (state, action) => {
  const newState = { ...state.common.errors }
  delete newState[action.payload]
  state.common.errors = newState
}

const resetCommonFormErrorsHandler = (state, action) => {
  state.common.errors = {}
}

const updateCommonConfigurationHandler = (state, action) => {
  state.common = {
    ...state.common,
    ...action.payload,
  }
}

const updateZohoModuleConfigurationHandler = (state, action) => {
  state.zohoModule = {
    ...state.zohoModule,
    ...action.payload,
  }
}

const updateCustomerModuleConfigurationHandler = (state, action) => {
  state.customerModule = {
    ...state.customerModule,
    ...action.payload,
  }
}

const updateCharacteristicsModuleConfigurationHandler = (state, action) => {
  state.characteristicsModule = {
    ...state.characteristicsModule,
    ...action.payload,
  }
}

const updateConsumptionModuleConfigurationHandler = (state, action) => {
  state.consumptionModule = {
    ...state.consumptionModule,
    ...action.payload,
  }
}

const updateOfferModuleConfigurationHandler = (state, action) => {
  state.offerModule = {
    ...state.offerModule,
    ...action.payload,
  }
}

const updateElectricityMeterModuleConfigurationHandler = (state, action) => {
  state.electricityMeterModule = {
    ...state.electricityMeterModule,
    ...action.payload,
  }
}

const updateGasMeterModuleConfigurationHandler = (state, action) => {
  state.gasMeterModule = {
    ...state.gasMeterModule,
    ...action.payload,
  }
}

const updateProviderConfigurationHandler = (state, action) => {
  state.providerConfig = {
    ...state.providerConfig,
    ...action.payload,
  }
}

const updateOfferConfigurationHandler = (state, action) => {
  state.offerConfig = {
    ...state.offerConfig,
    ...action.payload,
  }
}

const updateBankingsModuleConfigurationHandler = (state, action) => {
  state.bankingModule = {
    ...state.bankingModule,
    ...action.payload,
  }
}

const updatePropaneModuleConfigurationHandler = (state, action) => {
  state.propaneModule = {
    ...state.propaneModule,
    ...action.payload,
  }
}

const updateAdditionalSalesModuleConfigurationHandler = (state, action) => {
  state.additionalSales = {
    ...state.additionalSales,
    ...action.payload,
  }
}

export const SubscriptionFormConfigurationSlice = createSlice({
  name: 'subscriptionFormConfiguration',
  initialState,
  reducers: {
    initialFormConfigurationStateUpdate: initialFormConfigurationUpdateStateHandler,
    updateCommonConfiguration: updateCommonConfigurationHandler,
    updateCustomerModuleConfiguration: updateCustomerModuleConfigurationHandler,
    updateCharacteristicsModuleConfiguration: updateCharacteristicsModuleConfigurationHandler,
    updateBankingsModuleConfiguration: updateBankingsModuleConfigurationHandler,
    updateZohoModuleConfiguration: updateZohoModuleConfigurationHandler,
    updateProviderConfiguration: updateProviderConfigurationHandler,
    updateOfferConfiguration: updateOfferConfigurationHandler,
    updateOfferModuleConfiguration: updateOfferModuleConfigurationHandler,
    addCommonFormErrors: addCommonFormErrorsHandler,
    deleteCommonFormErrors: deleteCommonFormErrorsHandler,
    resetCommonFormErrors: resetCommonFormErrorsHandler,
    updateElectricityMeterModuleConfiguration: updateElectricityMeterModuleConfigurationHandler,
    updateAdditionalSalesModuleConfiguration: updateAdditionalSalesModuleConfigurationHandler,
    updateConsumptionModuleConfiguration: updateConsumptionModuleConfigurationHandler,
    updatePropaneModuleConfiguration: updatePropaneModuleConfigurationHandler,
    updateGasMeterModuleConfiguration: updateGasMeterModuleConfigurationHandler,
  },
})

export const {
  initialFormConfigurationStateUpdate,
  updateCommonConfiguration,
  updateCustomerModuleConfiguration,
  updateCharacteristicsModuleConfiguration,
  updateBankingsModuleConfiguration,
  updateZohoModuleConfiguration,
  updateProviderConfiguration,
  updateOfferConfiguration,
  updateOfferModuleConfiguration,
  addCommonFormErrors,
  deleteCommonFormErrors,
  resetCommonFormErrors,
  updateElectricityMeterModuleConfiguration,
  updateAdditionalSalesModuleConfiguration,
  updateConsumptionModuleConfiguration,
  updatePropaneModuleConfiguration,
  updateGasMeterModuleConfiguration,
} = SubscriptionFormConfigurationSlice.actions

export default SubscriptionFormConfigurationSlice.reducer
